import React, { useContext, useEffect, useSyncExternalStore } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { connectionSnapshot, parseConnection, subscribeConnection } from "src/common/whatsappConnection";
import { AuthContext } from "../context/AuthContext";
import ProtectedRoute from "./ProtectedRoute";
import StageSidebar from "src/stage/StageSidebar";
import Conversations from "src/features/Conversations/Conversations";
import Contact from "src/features/Contact/Contact";
import TemplatePage from "src/features/TemplatePage/TemplatePage";
import Template from "src/features/Template/Template";
import Tags from "src/features/Tags/Tags";
import Dashboard from "src/features/Dashboard/Dashboard";
import Bots from "src/features/Bots/Bots";
import Setup from "src/features/Setup/SetUp";
import SettingsPage from "src/features/Settings/SettingsPage";
import BroadcastsPage from "src/features/Broadcasts/BroadcastsPage";
import BroadcastComposer from "src/features/Broadcasts/BroadcastComposer";
import BroadcastDetail from "src/features/Broadcasts/BroadcastDetail";
import AutoAppyLogin from "src/features/Auth/AutoAppyLogin";
import Profile from "src/components/Profile";
import DeveloperOAuthCallback from "src/features/Auth/DeveloperOAuthCallback";
import WhatsAppGuide from "src/features/Guide/WhatsAppGuide";
import MetaBillingPage from 'src/features/MetaBilling/MetaBillingPage';

// AUTO-WA-39: one Stage shell (sidebar + page) for every signed-in page.
// Old URLs (/api-keys, /payment, /settings/billing, /flows, /settings, /broadcasts) still work.
const NO_SHELL = ["/", "/login", "/signup", "/auth/error", "/auth/autoappy/developer/callback"];
const FIXED_HEIGHT = ["/conversation"];

function LegacyBroadcastRedirect() {
  const { broadcastId } = useParams();
  return <Navigate to={`/broadcast/${broadcastId}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const { auth } = useContext(AuthContext);
  const storedConnection = useSyncExternalStore(subscribeConnection, connectionSnapshot);
  const connection = parseConnection(storedConnection);
  const showShell = auth && !NO_SHELL.includes(location.pathname);
  const fixed = FIXED_HEIGHT.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  // A new page starts at the top, like a normal page load.
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const routes = (
    <Routes>
      <Route path="/" element={<AutoAppyLogin />} />
      <Route path="/auth/error" element={<AutoAppyLogin />} />
      <Route path="/auth/autoappy/developer/callback" element={<DeveloperOAuthCallback />} />
      <Route path="/guide" element={<WhatsAppGuide />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/setup" element={<Setup />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/conversation" element={<Conversations />} />
        <Route path="/contacts" element={<Contact />} />
        <Route path="/templates" element={<Template />} />
        <Route path="/templates/create" element={<TemplatePage mode="create" />} />
        <Route path="/templates/:templateId/view" element={<TemplatePage mode="view" />} />
        <Route path="/templates/:templateId/edit" element={<TemplatePage mode="edit" />} />
        <Route path="/broadcast" element={<BroadcastsPage />} />
        <Route path="/broadcast/new" element={<BroadcastComposer />} />
        <Route path="/broadcast/:broadcastId" element={<BroadcastDetail />} />
        <Route path="/broadcasts" element={<Navigate to="/broadcast" replace />} />
        <Route path="/broadcasts/:broadcastId" element={<LegacyBroadcastRedirect />} />
        <Route path="/bots" element={<Bots />} />
        <Route path="/bots/:botId" element={<Navigate to="/bots?legacy=1" replace />} />
        <Route path="/chatbots" element={<Navigate to="/bots?legacy=1" replace />} />
        <Route path="/chatbots/:chatbotId" element={<Navigate to="/bots?legacy=1" replace />} />
        <Route path="/flows" element={<Navigate to="/bots?kind=guided" replace />} />
        <Route path="/tags" element={<Tags />} />
        <Route path="/meta-billing" element={<MetaBillingPage />} />
        <Route path="/meta-billing/:tab" element={<MetaBillingPage />} />
        <Route path="/settings/billing" element={<Navigate to="/meta-billing" replace />} />
        <Route path="/settings" element={<Navigate to="/settings/connection" replace />} />
        <Route path="/settings/:section" element={<SettingsPage />} />
        <Route path="/api-keys" element={<Navigate to="/settings/api-keys" replace />} />
        <Route path="/payment" element={<Navigate to="/meta-billing" replace />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
    </Routes>
  );

  if (!showShell) {
    return <div className="min-h-screen w-full bg-[#F2F6F8] text-[#102F45]">{routes}</div>;
  }

  return (
    <div className="wa-shell">
      <StageSidebar connection={connection} />
      <div className={`wa-shell-main${fixed ? " is-fixed wa-legacy" : ""}`}>{routes}</div>
    </div>
  );
}

export default AppRoutes;
